// COPYRIGHT SMARTTRACK

/**
 * Requires
 */

const moment = require('moment-timezone');
const QRCode = require('qrcode');
const inputModule = require('../modules/input');
const externalUserModule = require('../modules/external-user');
const userModule = require('../modules/user');
const helperModule = require('../modules/helper');
const appModule = require('../modules/app');
const scannerModule = require('../modules/scanner');
const { getCookie, formatDateTime } = require('../modules/utilities');


/**
 * State
 */

let assetsGlobalAvailableTypes;
let assetsGlobalSelectedFilter;
let assetsGlobalSelectedSortDict;
let assetsGlobalTableState;
let assetsGlobalSearchTimeout;
let assetsGlobalConfiguration;
let isSettingUpStickHeaders = false;

/**
 * Quick Scan State
 */

let quickScanTimeout;
let lastScannedCode = null;
let quickScanCurrentAsset = null;
let quickScanCurrentScannedCode = null;
let quickScanAvailablePersonnel = [];


/**
 * Quick Actions State
 */

let assetsGlobalActiveQuickAction = 'allAssets';

const QUICK_ACTIONS = [
	{ id: 'allAssets', label: 'All assets', fetchPayload: {} },
	{ id: 'checkedIn', label: 'Checked in', fetchPayload: { isActive: true, isPending: false } },
	{ id: 'checkedOut', label: 'Checked out', fetchPayload: { isActive: true, isPending: true } },
	{ id: 'recentlyScanned', label: 'Recently scanned', fetchPayload: { isActive: false } }
];


/**
 * Create Asset Modal State
 */

let createAssetAvailableTypes = [];
let createAssetScannedBookData = null;
let createAssetScannerTimeout;
let createAssetLastScannedCode = null;


/**
 * Drag State
 */

let dragX;
let dragY;
let dragTop;
let dragLeft;
let dragDown;
let dragElement;
let isDragging = false;


/**
 * Tooltip State
 */

let tooltipElement = null;
let tooltipImageHolder = null;
let tooltipImage = null;
let tooltipFieldsHolder = null;
let tooltipCurrentAssetId = null;
let tooltipHideTimeout = null;


/**
 * Global State
 */

window.assetsGlobalTotalNumber = 0;
window.assetsShouldReloadAssets = false;


/**
 * Handlers
 */

const updateAssetDisplay = () => {

	// Get user role
	const userRole = getCookie('userRole');

	// Get elements
	const holder = document.getElementById('assets-table-holder');
	const titleNumber = document.getElementById('assets-title-number');
	const removeButton = document.getElementById('assets-remove-button');

	// Create available types array
	let availableTypes = JSON.parse(JSON.stringify(assetsGlobalAvailableTypes));
	if (assetsGlobalSelectedFilter != null) availableTypes = [assetsGlobalSelectedFilter];

	// Ensure element is loaded
	if (titleNumber != null) {

		// Get total count
		let count = 0;
		const types = JSON.parse(JSON.stringify(assetsGlobalAvailableTypes));
		for (let i = 0; i < types.length; i += 1) {
			const property = types[i].value;
			if (window.assetsGlobalAssetDictionary[property] != null) {
				count += window.assetsGlobalAssetDictionary[property].length;
			}
		}

		// Set asset count
		titleNumber.innerHTML = `(${inputModule.formatCountNumber(count)}/${inputModule.formatCountNumber(window.assetsGlobalTotalNumber)})`;
	}

	// Hide remove button
	if (removeButton != null) {
		removeButton.style.display = 'none';
		removeButton.enabled = true;
	}

	// Ensure element is loaded
	if (holder != null) {

		// Initialize content
		let htmlContent = '';

		// Create asset type collections
		for (let i = 0; i < availableTypes.length; i += 1) {

			// Get asset collection
			const type = availableTypes[i];
			let displayArray = window.assetsGlobalDisplayDictionary[type.value];
			if (displayArray == null) displayArray = [];
			const sortSchema = assetsGlobalSelectedSortDict[type.value];

			// Get schema for type
			const schema = JSON.parse(JSON.stringify(assetsGlobalConfiguration.display.type_schemas[type.value]));
			const columnArray = schema.columns;
			const trackStockThreshold = schema.track_stock_threshold;
			const hideIfEmpty = schema.hide_if_empty;

			// Check if should track check in/out status
			const scanSchema = JSON.parse(JSON.stringify(assetsGlobalConfiguration.scan.type_schemas[type.value]));
			const trackCheckInOut = scanSchema.track_check_in_out_status || false;

			// Skip if empty
			if (hideIfEmpty === true && displayArray.length === 0) {
				// eslint-disable-next-line no-continue
				continue;
			}

			// Create collection content
			let html = `<h3>Asset Type<span class='bubble'>${type.display}</span><span>(${displayArray.length})</span>
				<a id='assets-table-load-more-${type.value}' class='load-more-button'>Load More<i class="fa fa-arrow-right"
				style="margin-left:5px; font-size:11px;" aria-hidden="true"></i></a></h3>
				<div class='card-content'><div class='table-container' id='assets-table-view-${type.value}'><div>
				<table><thead><tr>`;

			// Append selector schema item
			let offset = 0;
			if (scanSchema.track_geolocation) {
				columnArray.unshift({
					type: 'touchpoint_current_state'
				});
				offset += 1;
			} else if (trackCheckInOut) {
				columnArray.unshift({
					type: 'check_in_out'
				});
				offset += 1;
			}
			if (userRole === 'leader' || userRole === 'super' || userRole === 'admin') {
				columnArray.unshift({
					type: 'selector'
				});
				offset += 1;
			}

			// Create column headers
			for (let j = 0; j < columnArray.length; j += 1) {
				const column = columnArray[j];
				let arrow = '';
				if (sortSchema.column === (j - offset)) {
					if (sortSchema.reverse === false) {
						arrow = " <img src='/img/icons/menu-arrow.png' class='sort-arrow' alt='Sort Arrow' title='Sort Arrow'>";
					} else {
						arrow = " <img src='/img/icons/menu-arrow-open.png' class='sort-arrow' alt='Sort Arrow' title='Sort Arrow'>";
					}
				}
				if (column.type === 'selector') {
					html += "<th style='width:80px;'></th>";
				} else if (column.type === 'touchpoint_current_state') {
					html += '<th>Status</th>';
				} else if (column.type === 'check_in_out') {
					html += '<th>Status</th>';
				} else {
					html += `<th name='asset-row-sort-element' data-type-value='${type.value}' data-offset='${(j - offset)}'>${column.label}${arrow}</th>`;
				}
			}
			html += '</tr></thead><tbody>';

			// Iterate through assets
			for (let j = 0; j < displayArray.length; j += 1) {

				// Get asset data
				const asset = displayArray[j];
				const { groups } = asset;
				const quantity = asset.quantity || 0;
				const quantityAvailable = asset.quantityAvailable || 0;

				// Calculate asset parameters
				const checkedOutQuantity = quantity - quantityAvailable;

				// Create new asset row
				html += `<tr name='asset-row-element' data-asset-id='${asset.id}' class='${j % 2 === 0 ? 'asset-row-even' : 'asset-row-odd'}'>`;
				for (let k = 0; k < columnArray.length; k += 1) {
					const column = columnArray[k];

					// Get content
					let fieldContent = '';
					if (column.type === 'identifier') {
						fieldContent = asset.identifier;
					} else if (column.type === 'user') {
						fieldContent = asset.user;
					} else if (column.type === 'organization') {
						fieldContent = asset.organization;
					} else if (column.type === 'scannedAt') {
						if (asset.scannedAt != null) {
							const date = new Date(asset.scannedAt);
							fieldContent = formatDateTime(date);
						} else {
							const date = new Date(asset.createdAt);
							fieldContent = formatDateTime(date);
						}
					} else if (column.type === 'createdAt') {
						const date = new Date(asset.createdAt);
						fieldContent = formatDateTime(date);
					} else {
						let refData = asset.data;
						if (column.ref != null) {
							for (let m = 0; m < column.ref.length; m += 1) {
								const ref = column.ref[m];
								const check = refData[ref];
								if (check != null) {
									if (Array.isArray(check)) {
										fieldContent = check;
									} else if (typeof check === 'string') {
										fieldContent = check;
									} else if (typeof check === 'object') {
										refData = check;
									}
								}
							}
						}
						if (fieldContent != null && fieldContent !== ''
							&& column.type === 'quantity' && trackStockThreshold === true) {

							// Get parameters
							let stockLevelThreshold = 3;
							if (asset.data.stockLevelThreshold != null) {
								const threshold = parseInt(asset.data.stockLevelThreshold, 10);
								if (!Number.isNaN(threshold)) stockLevelThreshold = threshold;
							}

							// Get difference
							const diff = parseInt(fieldContent, 10) - stockLevelThreshold;

							// Create difference badge
							if (diff === 0) {
								fieldContent += '<span class="emphasis red">At threshold</span>';
							} else if (diff < 0) {
								fieldContent += `<span class="emphasis red">${Math.abs(diff)} below threshold</span>`;
							} else {
								fieldContent += `<span class="emphasis green">${Math.abs(diff)} above threshold</span>`;
							}
						}
					}
					if (fieldContent != null && fieldContent !== '') {
						if (column.type === 'array' && fieldContent != null) fieldContent = fieldContent.join(', ');
						else if (column.type === 'choice' && fieldContent != null) {
							const { choices } = column;
							for (let m = 0; m < choices.length; m += 1) {
								if (choices[m].id === fieldContent) {
									fieldContent = choices[m].value;
									break;
								}
							}
						}
					} else fieldContent = '';

					// Set content
					if (column.type === 'selector') {
						html += (`<td style='width:80px; padding:0px 0px 0px 0px; display:flex; align-items:center; justify-content:center; height:42px; gap: 14px;'>
							</div>
							<div class='row-selector' name='asset-selector' id='asset-selector-${asset.id}'
							data-asset-id='${asset.id}'></div>
							<img src='/img/icons/qr-code.png' class='qr-code' alt='QR Code' title='QR Code'
							name='asset-qr-code' id='asset-qr-icon-${asset.id}' data-asset-id='${asset.id}' />
							</div>
							</td>`);
					} else if (column.type === 'touchpoint_current_state') {
						let statusColor = '--brand-orange';
						let statusLabel = 'No Confidence';
						if (asset.geolocationDistanceDiff == null) {
							statusColor = '--brand-inactive-gray';
							statusLabel = 'Pending';
						} else {
							statusLabel = helperModule.calculateGeoCompliance(asset.geolocationDistanceDiff, scanSchema.geolocation_thresholds);
							if (statusLabel === 'Strong Confidence') {
								statusColor = '--brand-green';
							} else if (statusLabel === 'Medium Confidence') {
								statusColor = '--brand-accent-yellow';
							} else if (statusLabel === 'Low Confidence') {
								statusColor = '--brand-orange';
							}
						}
						html += (`<td><div class='asset-status-indicator' style="background-color:var(${statusColor});"></div><span>${statusLabel}</span></td>`);
					} else if (column.type === 'check_in_out') {
						let statusColor = '--brand-green';
						let statusLabel = 'All checked in';
						if (quantity === 0) {
							statusColor = '--brand-green';
							statusLabel = 'All checked in';
						} else if (checkedOutQuantity === quantity) {
							statusColor = '--brand-orange';
							statusLabel = 'All checked out';
						} else if (checkedOutQuantity === 0) {
							statusColor = '--brand-green';
							statusLabel = 'All checked in';
						} else {
							statusColor = '--brand-accent-yellow';
							statusLabel = checkedOutQuantity === 1 ? '1 checked out' : `${checkedOutQuantity} checked out`;
						}
						html += (`<td><div class='asset-status-indicator' style="background-color:var(${statusColor});"></div><span>${statusLabel}</span></td>`);
					} else {
						html += (`<td>${fieldContent}</td>`);
					}
				}
				html += '</tr>';

				// Create groups markup
				if (groups.length > 0) {

					// Add markup
					html += `<tr class='${j % 2 === 0 ? 'asset-row-even' : 'asset-row-odd'}'><td></td><td colspan="100"><table class='asset-group-container'><tbody>`;

					// Iterate through groups
					for (let k = 0; k < groups.length; k += 1) {
						const group = groups[k];
						html += `<tr class='${j % 2 === 0 ? 'asset-row-even' : 'asset-row-odd'}'><td style="padding-left:0px; font-size:15px;"><span class="emphasis outline">${group.quantity} ${group.quantity === 1}</span> checked out to ${group.personnel.name}<span class="emphasis outline right">${`${moment(group.createdAt).format('MMM D, YYYY')} at ${moment(group.createdAt).format('h:mm a')}`}</span></td></tr>`;
					}

					// Add markup
					html += '</tbody></table></td></tr>';
				}
			}

			// Append html
			html += '</tbody></table>';
			htmlContent += (`${html}</div></div>`);

			// If no assets, append empty state
			if (displayArray.length === 0) {
				htmlContent += ("<h2 class='table-empty-state-text'>Scan or create an asset with this type to get started.</h2>");
			}
			htmlContent += '</div>';
		}

		// Set content
		holder.innerHTML = htmlContent;

		// Hide loading indicator
		const loadingIndicator = document.getElementById('assets-activity-indicator');
		if (loadingIndicator != null) {
			if (loadingIndicator != null) {
				loadingIndicator.remove();
			}
		}

		// Set current scroll position
		Object.keys(assetsGlobalTableState).forEach((property) => {
			if (Object.prototype.hasOwnProperty.call(assetsGlobalTableState, property)) {
				const { scroll } = assetsGlobalTableState[property];
				$(`#assets-table-view-${property}`).scrollLeft(scroll);
			}
		});

		// Set up table sticky table headers
		$('#assets-table-holder table').stickyTableHeaders({
			fixedOffset: $('#header-section .header'),
			cacheHeaderHeight: true
		});

		// Handle action on enable sticky header
		$('#assets-table-holder table').on('enabledStickiness.stickyTableHeaders', function () {

			// Set state
			isSettingUpStickHeaders = true;

			// Update header width
			const width = $(this).parent().width();
			$(this).find('.tableFloatingHeaderOriginal').each(function () {
				$(this).attr('style', `${$(this).attr('style')}; width: ${width}px !important`);
			});

		});

		// Handle actions on header width update
		$('#assets-table-holder table').on('updatedWidth.stickyTableHeaders', function () {

			// Set state
			isSettingUpStickHeaders = false;

			// Get current scroll value
			const scrollLeft = $(this).parent().parent().scrollLeft();

			// Update header width
			const width = $(this).parent().width();
			$(this).find('.tableFloatingHeaderOriginal').each(function () {
				$(this).attr('style', `${$(this).attr('style')}; width: ${width}px !important`);
				if (scrollLeft > 0) $(this).scrollLeft(scrollLeft);
			});
		});

		// Handle actions on manual header scroll
		$('#assets-table-holder table thead').scroll(function () {
			if (this.scrollLeft > 0 || isSettingUpStickHeaders !== true) {
				$(this).parent().parent().parent()
					.scrollLeft(this.scrollLeft);
			}
		});

		// Handle actions on manual table scroll
		$('#assets-table-holder table thead').parent().parent().parent()
			.scroll(function () {
				if (this.scrollLeft > 0 || isSettingUpStickHeaders !== true) {
					$(this).find('table thead').scrollLeft(this.scrollLeft);
				}
			});
	}
};

/**
 * Tooltip Handlers
 */

const initializeTooltip = () => {
	tooltipElement = document.getElementById('asset-preview-tooltip');
	tooltipImageHolder = document.getElementById('asset-preview-tooltip-image-holder');
	tooltipImage = document.getElementById('asset-preview-tooltip-image');
	tooltipFieldsHolder = document.getElementById('asset-preview-tooltip-fields');
};

const getAssetById = (assetId) => {
	// Search through all asset types in the dictionary
	for (const type in window.assetsGlobalAssetDictionary) {
		if (Object.prototype.hasOwnProperty.call(window.assetsGlobalAssetDictionary, type)) {
			const assets = window.assetsGlobalAssetDictionary[type];
			const found = assets.find((asset) => asset.id === assetId);
			if (found) return { asset: found, type };
		}
	}
	return null;
};

const findImageFieldInSchema = (schema) => {
	// Look for image type fields in schema input array
	if (!schema || !schema.input) return null;
	return schema.input.find((field) => field.type === 'image');
};

const getAssetImageUrl = (asset, schema) => {
	const imageField = findImageFieldInSchema(schema);
	if (!imageField || !imageField.ref || !asset.data) return null;

	// Navigate ref array to get image value
	let data = asset.data;
	for (let i = 0; i < imageField.ref.length; i += 1) {
		const ref = imageField.ref[i];
		if (data[ref] !== undefined) {
			if (typeof data[ref] === 'string') {
				return data[ref].replace('http://', 'https://');
			}
			data = data[ref];
		}
	}
	return null;
};

const getFieldValue = (asset, field) => {
	if (!field.ref || !asset.data) return '';

	let data = asset.data;
	let value = '';

	for (let i = 0; i < field.ref.length; i += 1) {
		const ref = field.ref[i];
		if (data[ref] !== undefined) {
			if (Array.isArray(data[ref])) {
				value = data[ref].join(', ');
			} else if (typeof data[ref] === 'string') {
				value = data[ref];
			} else if (typeof data[ref] === 'object') {
				data = data[ref];
			}
		}
	}

	// Handle choice type fields
	if (field.type === 'choice' && field.choices && value) {
		const choice = field.choices.find((c) => c.id === value);
		if (choice) value = choice.value;
	}

	return value;
};

const buildTooltipContent = (asset, type) => {
	// Get schema for this asset type
	const schema = assetsGlobalConfiguration.scan.type_schemas[type];
	if (!schema || !schema.input) return;

	// Handle image
	const imageUrl = getAssetImageUrl(asset, schema);
	if (imageUrl) {
		tooltipImage.src = imageUrl;
		tooltipImageHolder.classList.add('has-image');
	} else {
		tooltipImage.src = '';
		tooltipImageHolder.classList.remove('has-image');
	}

	// Build fields HTML
	let fieldsHtml = '';

	// Add identifier field first
	const identifierLabel = (assetsGlobalConfiguration.scan.general && assetsGlobalConfiguration.scan.general.prompt)
		? assetsGlobalConfiguration.scan.general.prompt
		: 'Asset ID';
	fieldsHtml += `
		<div class="asset-preview-tooltip-field">
			<div class="asset-preview-tooltip-field-label">${identifierLabel}</div>
			<div class="asset-preview-tooltip-field-value">${asset.identifier || '<span class="empty">Not set</span>'}</div>
		</div>
	`;

	// Add schema fields (excluding image types)
	for (let i = 0; i < schema.input.length; i += 1) {
		const field = schema.input[i];
		if (field.type === 'image') continue;

		const value = getFieldValue(asset, field);
		const displayValue = value || '<span class="empty">Not set</span>';
		const label = field.prompt || field.label || field.id;

		fieldsHtml += `
			<div class="asset-preview-tooltip-field">
				<div class="asset-preview-tooltip-field-label">${label}</div>
				<div class="asset-preview-tooltip-field-value">${displayValue}</div>
			</div>
		`;
	}

	tooltipFieldsHolder.innerHTML = fieldsHtml;
};

const positionTooltip = (event) => {
	if (!tooltipElement) return;

	const tooltipRect = tooltipElement.getBoundingClientRect();
	const viewportWidth = window.innerWidth;
	const viewportHeight = window.innerHeight;

	const offset = 15; // Distance from cursor
	let left = event.clientX + offset;
	let top = event.clientY + offset;

	// Check right edge
	if (left + tooltipRect.width > viewportWidth - 10) {
		left = event.clientX - tooltipRect.width - offset;
	}

	// Check bottom edge
	if (top + tooltipRect.height > viewportHeight - 10) {
		top = event.clientY - tooltipRect.height - offset;
	}

	// Ensure not off left edge
	if (left < 10) left = 10;

	// Ensure not off top edge
	if (top < 10) top = 10;

	tooltipElement.style.left = `${left}px`;
	tooltipElement.style.top = `${top}px`;
};

const showTooltip = (assetId, event) => {
	// Cancel any pending hide
	if (tooltipHideTimeout) {
		clearTimeout(tooltipHideTimeout);
		tooltipHideTimeout = null;
	}

	// Skip if already showing this asset
	if (tooltipCurrentAssetId === assetId) {
		positionTooltip(event);
		return;
	}

	// Get asset data
	const result = getAssetById(assetId);
	if (!result) return;

	tooltipCurrentAssetId = assetId;

	// Build content
	buildTooltipContent(result.asset, result.type);

	// Position and show
	positionTooltip(event);
	tooltipElement.classList.add('visible');
};

const hideTooltip = () => {
	// Small delay to allow moving to adjacent rows smoothly
	tooltipHideTimeout = setTimeout(() => {
		if (tooltipElement) {
			tooltipElement.classList.remove('visible');
		}
		tooltipCurrentAssetId = null;
	}, 50);
};


const updateAssetsForUser = async (shouldReload, typeArray, page) => {

	// Initialize dictionaries
	window.assetsGlobalAssetDictionary = {};
	window.assetsGlobalDisplayDictionary = {};

	// Get assets for user
	const assets = await userModule.getAssetsForUser(shouldReload, typeArray, page);

	// Set assets to dictionaries
	for (let i = 0; i < assets.length; i += 1) {
		const asset = assets[i];

		// Get asset count
		if (i === assets.length - 1) window.assetsGlobalTotalNumber = asset.assetCount;

		// Set assets
		let typeArr = window.assetsGlobalAssetDictionary[asset.type];
		if (typeArr == null) typeArr = [];
		typeArr.push(asset);
		window.assetsGlobalAssetDictionary[asset.type] = typeArr;
	}

	// Sort type array by largest number of assets
	assetsGlobalAvailableTypes.sort((a, b) => {
		if (window.assetsGlobalAssetDictionary[a.value] == null) window.assetsGlobalAssetDictionary[a.value] = [];
		if (window.assetsGlobalAssetDictionary[b.value] == null) window.assetsGlobalAssetDictionary[b.value] = [];
		return window.assetsGlobalAssetDictionary[b.value].length - window.assetsGlobalAssetDictionary[a.value].length;
	});

	// Copy data to display dictionary
	window.assetsGlobalDisplayDictionary = JSON.parse(JSON.stringify(window.assetsGlobalAssetDictionary));

	// Update asset display
	$(document).ready(() => {
		updateAssetDisplay();
	});
};

const loadMoreAssets = (type) => {

	// Create type array
	const typeArray = [type];

	// Update assets for user
	updateAssetsForUser(true, typeArray, window.assetsGlobalPageNumberDict[type] + 1);
};

const loadAllAssets = async () => {

	// Initialize dictionaries
	window.assetsGlobalAssetDictionary = {};
	window.assetsGlobalDisplayDictionary = {};

	// Fetch all assets
	const assets = await userModule.fetchAssetsForUser(true);

	// Set assets to dictionaries
	for (let i = 0; i < assets.length; i += 1) {
		const asset = assets[i];

		// Get asset count
		if (i === assets.length - 1) window.assetsGlobalTotalNumber = asset.assetCount;

		// Set assets
		let typeArray = window.assetsGlobalAssetDictionary[asset.type];
		if (typeArray == null) typeArray = [];
		typeArray.push(asset);
		window.assetsGlobalAssetDictionary[asset.type] = typeArray;
	}

	// Sort type array by largest number of assets
	assetsGlobalAvailableTypes.sort((a, b) => {
		if (window.assetsGlobalAssetDictionary[a.value] == null) window.assetsGlobalAssetDictionary[a.value] = [];
		if (window.assetsGlobalAssetDictionary[b.value] == null) window.assetsGlobalAssetDictionary[b.value] = [];
		return window.assetsGlobalAssetDictionary[b.value].length - window.assetsGlobalAssetDictionary[a.value].length;
	});

	// Copy data to display dictionary
	window.assetsGlobalDisplayDictionary = JSON.parse(JSON.stringify(window.assetsGlobalAssetDictionary));

	// Update asset display
	$(document).ready(() => {
		updateAssetDisplay();
	});
};

const setupAssetFilter = () => {

	// Get filter elements
	$(document).ready(() => {
		const elements = document.getElementsByName('assets-type-filter');
		for (let i = 0; i < elements.length; i += 1) {
			const element = elements[i];

			// Ensure element is loaded
			if (element.children.length <= 1) {

				// Create asset type filter options
				let html = '';
				for (let j = 0; j < assetsGlobalAvailableTypes.length; j += 1) {

					// Build new option
					html += (`<option value='${assetsGlobalAvailableTypes[j].value.replace(/"/g, '&#34;').replace(/'/g, '&#39;')}'>${assetsGlobalAvailableTypes[j].display}</option>`);
				}

				// Append option to filter
				element.innerHTML += html;
			}

		}
	});
};


/**
 * Quick Actions Handlers
 */

const updateQuickActionScrollState = () => {
	const wrapper = document.getElementById('assets-quick-actions-wrapper');
	const leftBtn = document.getElementById('assets-quick-actions-left');
	const rightBtn = document.getElementById('assets-quick-actions-right');

	if (!wrapper || !leftBtn || !rightBtn) return;

	const canScrollLeft = wrapper.scrollLeft > 0;
	const canScrollRight = wrapper.scrollLeft + wrapper.clientWidth < wrapper.scrollWidth - 1;

	// Update button visibility
	if (canScrollLeft) {
		leftBtn.classList.add('visible');
	} else {
		leftBtn.classList.remove('visible');
	}

	if (canScrollRight) {
		rightBtn.classList.add('visible');
	} else {
		rightBtn.classList.remove('visible');
	}

	// Update wrapper mask classes
	wrapper.classList.remove('mask-left', 'mask-right', 'mask-both');
	if (canScrollLeft && canScrollRight) {
		wrapper.classList.add('mask-both');
	} else if (canScrollLeft) {
		wrapper.classList.add('mask-left');
	} else if (canScrollRight) {
		wrapper.classList.add('mask-right');
	}
};

const clearActiveQuickAction = () => {
	assetsGlobalActiveQuickAction = null;
	const items = document.querySelectorAll('.quick-action-item');
	items.forEach((item) => {
		item.classList.remove('active');
	});
};

const selectQuickAction = (actionId) => {

	// Update active state
	assetsGlobalActiveQuickAction = actionId;

	// Find the action configuration
	const action = QUICK_ACTIONS.find((a) => a.id === actionId);
	if (!action) return;

	// Update visual active state
	const items = document.querySelectorAll('.quick-action-item');
	items.forEach((item) => {
		if (item.dataset.actionId === actionId) {
			item.classList.add('active');
		} else {
			item.classList.remove('active');
		}
	});

	// Clear existing filters (type filter and search)
	const typeFilter = document.getElementById('assets-type-filter');
	if (typeFilter) {
		typeFilter.value = '';
		assetsGlobalSelectedFilter = null;
	}

	const searchInput = document.getElementById('asset-search-input-large');
	if (searchInput) {
		searchInput.value = '';
	}

	// Apply the quick action filter
	applyQuickActionFilter(action.fetchPayload);
};

const applyQuickActionFilter = async (payload) => {

	// Show loading indicator
	const loadingIndicator = document.getElementById('assets-activity-indicator');
	if (loadingIndicator) {
		loadingIndicator.style.display = 'block';
	}

	// For now, if no payload filters, just reset to show all loaded assets
	// Backend support for isActive/isPending filters is needed for full functionality
	if (Object.keys(payload).length === 0) {

		// "All assets" - reset display to show all assets
		window.assetsGlobalDisplayDictionary = JSON.parse(JSON.stringify(window.assetsGlobalAssetDictionary));
		updateAssetDisplay();
		if (loadingIndicator) {
			loadingIndicator.style.display = 'none';
		}
		return;
	}

	// For filtered quick actions, perform client-side filtering based on available data
	// Note: Full backend support needed for isActive/isPending server-side filtering
	try {

		// Client-side filtering based on existing asset data
		const filteredDict = {};

		Object.keys(window.assetsGlobalAssetDictionary).forEach((property) => {
			if (Object.prototype.hasOwnProperty.call(window.assetsGlobalAssetDictionary, property)) {
				const assetArray = window.assetsGlobalAssetDictionary[property] || [];
				const filteredArray = assetArray.filter((asset) => {

					// Check each filter condition
					let matches = true;
					if (payload.isActive !== undefined && asset.isActive !== payload.isActive) {
						matches = false;
					}
					if (payload.isPending !== undefined && asset.isPending !== payload.isPending) {
						matches = false;
					}
					return matches;
				});
				filteredDict[property] = filteredArray;
			}
		});

		window.assetsGlobalDisplayDictionary = filteredDict;
		updateAssetDisplay();

	} catch (error) {
		// eslint-disable-next-line no-console
		console.error('Error applying quick action filter:', error);
		if (error && error.code === 209) {
			externalUserModule.handleUserLogOut(true);
		}
	}

	// Hide loading indicator
	if (loadingIndicator) {
		loadingIndicator.style.display = 'none';
	}
};


const filterAssetType = (element) => {

	// Clear active quick action when using type filter
	clearActiveQuickAction();

	// Get selected option
	assetsGlobalSelectedFilter = {
		value: element.options[element.selectedIndex].value,
		display: element.options[element.selectedIndex].text
	};
	if (assetsGlobalSelectedFilter.value === '') assetsGlobalSelectedFilter = null;

	// Update asset display
	updateAssetDisplay();
};

const performGlobalAssetSearch = (searchValue) => {

	// Perform global search
	Parse.Cloud.run('performAssetSearchWithParameters', { searchText: searchValue }).then((assets) => {

		// Create asset dict
		const assetDict = {};
		for (let i = 0; i < assets.length; i += 1) {
			const asset = assets[i];

			// Set assets
			let typeArray = assetDict[asset.type];
			if (typeArray == null) typeArray = [];
			typeArray.push(asset);
			assetDict[asset.type] = typeArray;
		}

		// Iterate through asset types
		Object.keys(window.assetsGlobalDisplayDictionary).forEach((property) => {
			if (Object.prototype.hasOwnProperty.call(window.assetsGlobalDisplayDictionary, property)) {
				let displayArray = (window.assetsGlobalDisplayDictionary[property] != null) ? window.assetsGlobalDisplayDictionary[property] : [];
				const assetArray = (assetDict[property] != null) ? assetDict[property] : [];
				if (displayArray != null) {

					// Filter out existing assets
					for (let i = 0; i < assetArray.length; i += 1) {
						const asset = assetArray[i];
						let found = false;
						for (let j = 0; j < displayArray.length; j += 1) {
							const testAsset = displayArray[j];
							const { id } = asset;
							const testId = testAsset.id;
							if (id != null && testId != null) {
								if (id === testId) found = true;
							}
						}
						if (found === false) {
							displayArray.push(asset);
						}
					}
				} else {
					displayArray = [...assetArray];
				}
				window.assetsGlobalDisplayDictionary[property] = [...displayArray];
			}
		});

		// Update asset display
		updateAssetDisplay();
	});
};

const performAssetSearch = (element) => {

	// Cancel global search timer
	if (assetsGlobalSearchTimeout != null) clearTimeout(assetsGlobalSearchTimeout);

	// Get search value
	let searchValue = element.value;

	// Clear active quick action when search has value
	if (searchValue !== '') {
		clearActiveQuickAction();
	}

	// If search value is empty, reset display array
	if (searchValue === '') {
		window.assetsGlobalDisplayDictionary = JSON.parse(JSON.stringify(window.assetsGlobalAssetDictionary));
	} else {

		// Format search value
		searchValue = searchValue.toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');

		// Update values matching search string
		Object.keys(window.assetsGlobalAssetDictionary).forEach((property) => {
			if (Object.prototype.hasOwnProperty.call(window.assetsGlobalAssetDictionary, property)) {
				const displayArray = window.assetsGlobalAssetDictionary[property];
				const newDisplayArray = [];
				if (displayArray != null) {
					for (let i = 0; i < displayArray.length; i += 1) {
						const { searchTag } = displayArray[i];
						if (searchTag.indexOf(searchValue) > -1) newDisplayArray.push(displayArray[i]);
					}
				}
				window.assetsGlobalDisplayDictionary[property] = newDisplayArray;
			}
		});

		// Create global search timer
		assetsGlobalSearchTimeout = setTimeout(() => {
			performGlobalAssetSearch(searchValue);
		}, 800);
	}

	// Update asset display
	updateAssetDisplay();
};

const performAssetSort = (type, columnIndex) => {

	// Update selected sort
	let reverse = false;
	if (assetsGlobalSelectedSortDict[type].column === columnIndex) {
		if (assetsGlobalSelectedSortDict[type].reverse === false) {
			assetsGlobalSelectedSortDict[type].reverse = true;
			reverse = true;
		} else assetsGlobalSelectedSortDict[type].reverse = false;
	} else {
		assetsGlobalSelectedSortDict[type].column = columnIndex;
		assetsGlobalSelectedSortDict[type].reverse = false;
	}

	// Get sort column from schema
	const columnArray = assetsGlobalConfiguration.display.type_schemas[type].columns;
	const column = columnArray[columnIndex];

	// Sort display array
	window.assetsGlobalDisplayDictionary[type].sort((a, b) => {

		// Get content
		let aContent = '';
		let bContent = '';
		if (column.type === 'identifier') {
			aContent = a.identifier;
			bContent = b.identifier;
		} else if (column.type === 'user') {
			aContent = a.user;
			bContent = b.user;
		} else if (column.type === 'organization') {
			aContent = a.organization;
			bContent = b.organization;
		} else if (column.type === 'scannedAt') {
			if (a.scannedAt != null) aContent = a.scannedAt;
			else aContent = a.createdAt;
			if (b.scannedAt != null) bContent = b.scannedAt;
			else bContent = b.createdAt;
		} else if (column.type === 'createdAt') {
			aContent = a.createdAt;
			bContent = b.createdAt;
		} else {
			let aRefData = a.data;
			let bRefData = b.data;
			for (let m = 0; m < column.ref.length; m += 1) {
				const ref = column.ref[m];
				const aCheck = aRefData[ref];
				const bCheck = bRefData[ref];
				if (aCheck != null) {
					if (Array.isArray(aCheck)) {
						aContent = aCheck;
					} else if (typeof aCheck === 'string') {
						aContent = aCheck;
					} else if (typeof aCheck === 'object') {
						aRefData = aCheck;
					}
				}
				if (bCheck != null) {
					if (Array.isArray(bCheck)) {
						bContent = bCheck;
					} else if (typeof bCheck === 'string') {
						bContent = bCheck;
					} else if (typeof bCheck === 'object') {
						bRefData = bCheck;
					}
				}
			}
		}
		if (aContent != null && aContent !== '') {
			if (column.type === 'array' && aContent != null) aContent = aContent.join(', ');
			else if (column.type === 'choice' && aContent != null) {
				const { choices } = column;
				for (let m = 0; m < choices.length; m += 1) {
					if (choices[m].id === aContent) {
						aContent = choices[m].value;
						break;
					}
				}
			}
		} else aContent = '';
		if (bContent != null && bContent !== '') {
			if (column.type === 'array' && bContent != null) bContent = bContent.join(', ');
			else if (column.type === 'choice' && bContent != null) {
				const { choices } = column;
				for (let m = 0; m < choices.length; m += 1) {
					if (choices[m].id === bContent) {
						bContent = choices[m].value;
						break;
					}
				}
			}
		} else bContent = '';
		if (column.type === 'number' || column.type === 'quantity' || column.type === 'stockLevelThreshold') {
			aContent = parseInt(aContent, 10);
			bContent = parseInt(bContent, 10);
		} else if (column.type === 'price') {
			aContent = parseFloat(aContent);
			bContent = parseFloat(bContent);
		} else {
			if (aContent != null) aContent = aContent.toLowerCase();
			if (bContent != null) bContent = bContent.toLowerCase();
		}

		// Sort content
		if (!reverse) return (bContent < aContent) ? 1 : ((aContent < bContent) ? -1 : 0); // eslint-disable-line no-nested-ternary
		return (aContent < bContent) ? 1 : ((bContent < aContent) ? -1 : 0); // eslint-disable-line no-nested-ternary
	});

	// Update asset display
	updateAssetDisplay();
};

const selectAssetRow = (e, id) => {
	e.stopPropagation();

	// Select or deselect row
	if (document.getElementById(`asset-selector-${id}`).style.backgroundColor === '') {
		document.getElementById(`asset-selector-${id}`).style.backgroundColor = 'var(--brand-primary)';
	} else {
		document.getElementById(`asset-selector-${id}`).style.backgroundColor = '';
	}

	// Check selected counts
	let count = 0;
	const assets = document.getElementsByName('asset-selector');
	for (let i = 0; i < assets.length; i += 1) {
		if (assets[i].style.backgroundColor !== '') {
			count += 1;
		}
	}

	// Show or hide remove button
	if (count > 0) {
		document.getElementById('assets-remove-button').style.display = 'block';
		helperModule.handleRemoveButtonPosition();
	} else {
		document.getElementById('assets-remove-button').style.display = 'none';
	}
};

const removeAssets = () => {

	// Get user role
	const userRole = getCookie('userRole');

	// Validate user role
	if (userRole === 'leader' || userRole === 'super' || userRole === 'admin') {

		// Disable remove button
		document.getElementById('assets-remove-button').enabled = false;

		// Show remove confirmation
		inputModule.showModalWithId('asset-archive-confirmation');
	}
};

const handleRemoveAssets = (confirm) => {
	if (confirm === true) {

		// Hide remove confirmation
		inputModule.hideModalWithId('asset-archive-confirmation');

		// Initialize remove array
		const removeArray = [];

		// Get selected assets
		const assets = document.getElementsByName('asset-selector');
		if (assets.length > 0) {
			for (let i = assets.length - 1; i >= 0; i -= 1) {
				if (assets[i].style.backgroundColor !== '') {

					// Get asset id
					const assetId = assets[i].id.replace('asset-selector-', '');

					// Append to remove array
					removeArray.push(assetId);

					// Remove row from display
					assets[i].parentElement.parentElement.remove();
				}
			}

			// Hide remove button
			document.getElementById('assets-remove-button').enabled = true;
			document.getElementById('assets-remove-button').style.display = 'none';

			// Remove assets from asset dictionary
			const assetArray = [];
			Object.keys(window.assetsGlobalAssetDictionary).forEach((property) => {
				if (Object.prototype.hasOwnProperty.call(window.assetsGlobalAssetDictionary, property)) {
					const displayArray = window.assetsGlobalAssetDictionary[property];
					for (let i = displayArray.length - 1; i >= 0; i -= 1) {
						if (removeArray.indexOf(displayArray[i].id) > -1) {
							displayArray.splice(i, 1);
						}
					}
					Array.prototype.push.apply(assetArray, displayArray);
				}
			});

			// Remove assets from display dictionary
			Object.keys(window.assetsGlobalDisplayDictionary).forEach((property) => {
				if (Object.prototype.hasOwnProperty.call(window.assetsGlobalDisplayDictionary, property)) {
					const displayArray = window.assetsGlobalDisplayDictionary[property];
					for (let i = displayArray.length - 1; i >= 0; i -= 1) {
						if (removeArray.indexOf(displayArray[i].id) > -1) {
							displayArray.splice(i, 1);
						}
					}
				}
			});

			// Remove assets from asset array
			for (let i = window.assetsGlobalAssetArray.length - 1; i >= 0; i -= 1) {
				if (removeArray.indexOf(window.assetsGlobalAssetArray[i].id) > -1) {
					window.assetsGlobalAssetArray.splice(i, 1);
				}
			}

			// Subtract from total
			if (window.assetsGlobalTotalNumber != null) window.assetsGlobalTotalNumber -= removeArray.length;

			// Set asset array to session storage
			try {
				sessionStorage.setItem('user-assets', JSON.stringify(assetArray));
			} catch (e) { }

			// Archive assets
			Parse.Cloud.run('archiveAssetsWithParameters', { assetIDArray: removeArray, platform: 'web' }).then(() => {

				// Update asset display
				updateAssetDisplay();

			}).catch((error) => {
				if (error && error.code === 209) {
					externalUserModule.handleUserLogOut(true);
				} else {

					// Show error
					inputModule.showModalWithId('asset-archive-error-modal');
				}
			});
		} else {

			// Enable remove button
			document.getElementById('assets-remove-button').enabled = true;
		}
	} else {

		// Hide remove confirmation
		inputModule.hideModalWithId('asset-archive-confirmation');

		// Enable remove button
		document.getElementById('assets-remove-button').enabled = true;
	}
};

const exportAssets = async () => {

	// Show status modal
	inputModule.showModalWithId('asset-export-status-modal');

	// Export assets
	await Parse.Cloud.run('exportAssetsForUser', { platform: 'web' });

	// Set timeout for status modal
	setTimeout(() => {

		// Hide status modal
		inputModule.hideModalWithId('asset-export-status-modal');

	}, 6000);
};

const exportCheckoutReport = async () => {

	// Show status modal
	inputModule.showModalWithId('asset-export-status-modal');

	// Export assets
	await Parse.Cloud.run('exportReportForUser', { platform: 'web', type: 'checkout' });

	// Set timeout for status modal
	setTimeout(() => {

		// Hide status modal
		inputModule.hideModalWithId('asset-export-status-modal');

	}, 6000);
};

const exportAssetTouchpoints = async () => {

	// Get parameters
	const startDate = $('#asset-touchpoint-report-options-start-date').val();
	const endDate = $('#asset-touchpoint-report-options-end-date').val();

	// Validate parameters
	if (!startDate) {
		return;
	}
	if (!endDate) {
		return;
	}

	// Hide options modal
	inputModule.hideModalWithId('asset-touchpoint-report-options-modal');

	// Show status modal
	inputModule.showModalWithId('asset-export-status-modal');

	// Export assets
	await Parse.Cloud.run('exportAssetTouchpointsForUser', { platform: 'web', startDate: new Date(startDate), endDate: new Date(endDate) });

	// Set timeout for status modal
	setTimeout(() => {

		// Hide status modal
		inputModule.hideModalWithId('asset-export-status-modal');

	}, 6000);
};

const promptRunTouchpointReport = async () => {

	// Set default dates
	$('#asset-touchpoint-report-options-start-date').val(moment().subtract(1, 'week').format('YYYY-MM-DD[T]HH:mm'));
	$('#asset-touchpoint-report-options-end-date').val(moment().format('YYYY-MM-DD[T]HH:mm'));

	// Show status modal
	inputModule.showModalWithId('asset-touchpoint-report-options-modal');
};

const exportRunReport = async () => {

	// Get parameters
	const startDate = $('#asset-run-report-options-start-date').val();
	const endDate = $('#asset-run-report-options-end-date').val();

	// Validate parameters
	if (!startDate) {
		return;
	}
	if (!endDate) {
		return;
	}

	// Hide options modal
	inputModule.hideModalWithId('asset-run-report-options-modal');

	// Show status modal
	inputModule.showModalWithId('asset-export-status-modal');

	// Export report
	await Parse.Cloud.run('exportReportForUser', {
		platform: 'web',
		type: 'checkout',
		startDate: new Date(startDate),
		endDate: new Date(endDate)
	});

	// Set timeout for status modal
	setTimeout(() => {

		// Hide status modal
		inputModule.hideModalWithId('asset-export-status-modal');

	}, 6000);
};

const promptRunReport = async () => {

	// Set default dates (last week to now)
	$('#asset-run-report-options-start-date').val(moment().subtract(1, 'week').format('YYYY-MM-DD[T]HH:mm'));
	$('#asset-run-report-options-end-date').val(moment().format('YYYY-MM-DD[T]HH:mm'));

	// Show options modal
	inputModule.showModalWithId('asset-run-report-options-modal');
};

const importAssets = () => {
	window.origin_importFile = 'assets';
	appModule.handleRouting('/dashboard/import');
};

const addAsset = () => {
	appModule.handleRouting('/dashboard/asset/new');
};

const editAssetWithId = (id) => {
	appModule.handleRouting(`/dashboard/asset/${id}`);
};


/**
 * Quick Scan Handlers
 */

const handleQuickScanResult = async (scannedCode) => {

	// Prevent duplicate scans
	if (scannedCode === lastScannedCode) return;
	lastScannedCode = scannedCode;

	// Stop scanning
	scannerModule.stopScanning();

	// Show loading state
	document.getElementById('quick-scan-video-container').style.display = 'none';
	document.getElementById('quick-scan-status').style.display = 'block';
	document.getElementById('quick-scan-status-text').innerHTML = 'Searching for asset...';

	// Clear any previous timeout
	if (quickScanTimeout) clearTimeout(quickScanTimeout);

	// Search for asset
	try {
		const items = await Parse.Cloud.run('fetchAssetDataWithHint', {
			hintContent: scannedCode.toLowerCase().replace(/[^\p{L}\p{N}]/gu, ''),
			hintType: 'searchTag',
		});

		if (items && items.length > 0) {
			// Asset found - show preview in modal
			const asset = items[0];

			if (asset.source === 'asset') {
				// Existing asset - show preview with quick actions
				showQuickScanResult(asset, scannedCode, true);
			} else {
				// New asset suggestion - show preview with add button
				showQuickScanResult(asset, scannedCode, false);
			}
		} else {

			// Asset not found
			inputModule.hideModalWithId('quick-scan-modal');
			document.getElementById('quick-scan-not-found-text').innerHTML = `We couldn't find an asset matching "${scannedCode}".`;
			inputModule.showModalWithId('quick-scan-not-found-modal');
			resetQuickScanState();
		}
	} catch (error) {
		console.error('Quick scan search error:', error);
		document.getElementById('quick-scan-error-text').innerHTML = 'An error occurred while searching. Please try again.';
		document.getElementById('quick-scan-status').style.display = 'none';
		document.getElementById('quick-scan-error').style.display = 'block';

		// Auto-retry after 3 seconds
		quickScanTimeout = setTimeout(() => {
			startQuickScan();
		}, 3000);
	}
};

const resetQuickScanState = () => {
	lastScannedCode = null;
	document.getElementById('quick-scan-video-container').style.display = 'block';
	document.getElementById('quick-scan-status').style.display = 'none';
	document.getElementById('quick-scan-error').style.display = 'none';
	document.getElementById('quick-scan-result').style.display = 'none';
	document.getElementById('quick-scan-scan-again').style.display = 'none';
	quickScanCurrentAsset = null;
	quickScanCurrentScannedCode = null;
	if (quickScanTimeout) clearTimeout(quickScanTimeout);
};

const startQuickScan = async () => {
	resetQuickScanState();

	// Check camera permission
	const permission = await scannerModule.checkCameraPermission();

	if (permission === 'denied') {
		inputModule.showModalWithId('quick-scan-camera-error-modal');
		return;
	}

	// Show modal
	inputModule.showModalWithId('quick-scan-modal');

	// Start scanning
	const videoElement = document.getElementById('quick-scan-video');
	scannerModule.startScanning(videoElement, handleQuickScanResult, (err) => {
		console.error('Scanner error:', err);
	});
};

const stopQuickScan = () => {
	scannerModule.stopScanning();
	inputModule.hideModalWithId('quick-scan-modal');
	resetQuickScanState();
};


/**
 * Quick Scan Result Display Functions
 */

const showQuickScanResult = (asset, scannedCode, isExisting) => {
	// Store current state
	quickScanCurrentAsset = asset;
	quickScanCurrentScannedCode = scannedCode;

	// Hide scanning UI, show result UI
	document.getElementById('quick-scan-video-container').style.display = 'none';
	document.getElementById('quick-scan-status').style.display = 'none';
	document.getElementById('quick-scan-error').style.display = 'none';
	document.getElementById('quick-scan-result').style.display = 'block';
	document.getElementById('quick-scan-scan-again').style.display = 'block';

	// Update badge
	const badge = document.getElementById('quick-scan-result-badge-text');
	if (isExisting) {
		badge.textContent = 'Asset Found';
		badge.className = 'badge-existing';
	} else {
		badge.textContent = 'New Item';
		badge.className = 'badge-new';
	}

	// Build and display content
	if (isExisting) {
		buildExistingAssetPreview(asset);
	} else {
		buildNewAssetPreview(asset, scannedCode);
	}
};

const buildExistingAssetPreview = (asset) => {
	const type = asset.type;
	const schema = assetsGlobalConfiguration.scan.type_schemas[type];

	if (!schema) return;

	// Handle image
	const imageUrl = getAssetImageUrl(asset, schema);
	const imageHolder = document.getElementById('quick-scan-result-image-holder');
	const imageElement = document.getElementById('quick-scan-result-image');

	if (imageUrl) {
		imageElement.src = imageUrl;
		imageHolder.classList.add('has-image');
	} else {
		imageElement.src = '';
		imageHolder.classList.remove('has-image');
	}

	// Build fields HTML
	let fieldsHtml = '';

	// Add identifier field first
	const identifierLabel = (assetsGlobalConfiguration.scan.general && assetsGlobalConfiguration.scan.general.prompt)
		? assetsGlobalConfiguration.scan.general.prompt
		: 'Asset ID';
	fieldsHtml += `
		<div class="quick-scan-result-field">
			<div class="quick-scan-result-field-label">${identifierLabel}</div>
			<div class="quick-scan-result-field-value">${asset.identifier || '<span class="empty">Not set</span>'}</div>
		</div>
	`;

	// Add schema fields (excluding image types)
	if (schema.input) {
		for (let i = 0; i < schema.input.length; i += 1) {
			const field = schema.input[i];
			if (field.type === 'image') continue;

			const value = getFieldValue(asset, field);
			const displayValue = value || '<span class="empty">Not set</span>';
			const label = field.prompt || field.label || field.id;

			fieldsHtml += `
				<div class="quick-scan-result-field">
					<div class="quick-scan-result-field-label">${label}</div>
					<div class="quick-scan-result-field-value">${displayValue}</div>
				</div>
			`;
		}
	}

	document.getElementById('quick-scan-result-fields').innerHTML = fieldsHtml;

	// Handle checkout status display
	const checkoutStatusHolder = document.getElementById('quick-scan-checkout-status');
	const trackCheckInOut = schema.track_check_in_out_status || false;

	if (trackCheckInOut && asset.groups && asset.groups.length > 0) {
		let checkoutHtml = '<h5>Currently Checked Out</h5>';
		asset.groups.slice(0, 3).forEach((group) => {
			checkoutHtml += `
				<div class="quick-scan-checkout-row">
					<span class="name">${group.personnel?.name || 'Unknown'}</span>
					<span class="quantity">${group.quantity}</span>
				</div>
			`;
		});
		if (asset.groups.length > 3) {
			checkoutHtml += `<div class="quick-scan-checkout-row"><span class="name" style="color:#999;">+${asset.groups.length - 3} more</span></div>`;
		}
		document.getElementById('quick-scan-checkout-info').innerHTML = checkoutHtml;
		checkoutStatusHolder.style.display = 'block';
	} else {
		checkoutStatusHolder.style.display = 'none';
	}

	// Build action buttons
	buildExistingAssetActions(asset, schema);

	// Pre-fetch personnel if check in/out enabled
	if (trackCheckInOut && quickScanAvailablePersonnel.length === 0) {
		fetchQuickScanPersonnel();
	}
};

const buildNewAssetPreview = (asset, scannedCode) => {
	const imageHolder = document.getElementById('quick-scan-result-image-holder');
	const imageElement = document.getElementById('quick-scan-result-image');

	// Get schema for asset type if available
	const type = asset.type;
	const schema = type && assetsGlobalConfiguration.scan.type_schemas[type]
		? assetsGlobalConfiguration.scan.type_schemas[type]
		: null;

	// Handle image - check schema first, then fallback to book data
	let imageUrl = null;
	if (schema) {
		imageUrl = getAssetImageUrl(asset, schema);
	}
	if (!imageUrl && asset.data && asset.data.bookThumbnail) {
		imageUrl = asset.data.bookThumbnail.replace('http://', 'https://');
	}
	if (!imageUrl && asset.data && asset.data.thumbnail) {
		imageUrl = asset.data.thumbnail.replace('http://', 'https://');
	}

	if (imageUrl) {
		imageElement.src = imageUrl;
		imageHolder.classList.add('has-image');
	} else {
		imageElement.src = '';
		imageHolder.classList.remove('has-image');
	}

	// Build fields HTML
	let fieldsHtml = '';

	// Show scanned code / identifier
	const identifierLabel = (assetsGlobalConfiguration.scan.general && assetsGlobalConfiguration.scan.general.prompt)
		? assetsGlobalConfiguration.scan.general.prompt
		: 'Scanned Code';
	fieldsHtml += `
		<div class="quick-scan-result-field">
			<div class="quick-scan-result-field-label">${identifierLabel}</div>
			<div class="quick-scan-result-field-value">${asset.identifier || scannedCode}</div>
		</div>
	`;

	// If we have a schema, show all schema fields like existing assets
	if (schema && schema.input) {
		for (let i = 0; i < schema.input.length; i += 1) {
			const field = schema.input[i];
			if (field.type === 'image') continue;

			const value = getFieldValue(asset, field);
			const displayValue = value || '<span class="empty">Not set</span>';
			const label = field.prompt || field.label || field.id;

			fieldsHtml += `
				<div class="quick-scan-result-field">
					<div class="quick-scan-result-field-label">${label}</div>
					<div class="quick-scan-result-field-value">${displayValue}</div>
				</div>
			`;
		}
	} else if (asset.data) {
		// Fallback: show book data if no schema available
		if (asset.data.bookTitle) {
			fieldsHtml += `
				<div class="quick-scan-result-field">
					<div class="quick-scan-result-field-label">Title</div>
					<div class="quick-scan-result-field-value">${asset.data.bookTitle}</div>
				</div>
			`;
		}
		if (asset.data.bookAuthors) {
			const authorsText = Array.isArray(asset.data.bookAuthors)
				? asset.data.bookAuthors.join(', ')
				: asset.data.bookAuthors;
			fieldsHtml += `
				<div class="quick-scan-result-field">
					<div class="quick-scan-result-field-label">Author(s)</div>
					<div class="quick-scan-result-field-value">${authorsText}</div>
				</div>
			`;
		}
		if (asset.data.bookSource) {
			fieldsHtml += `
				<div class="quick-scan-result-field">
					<div class="quick-scan-result-field-label">Source</div>
					<div class="quick-scan-result-field-value">${asset.data.bookSource}</div>
				</div>
			`;
		}
	}

	document.getElementById('quick-scan-result-fields').innerHTML = fieldsHtml;
	document.getElementById('quick-scan-checkout-status').style.display = 'none';

	// Build action buttons for new asset
	buildNewAssetActions();
};

const buildExistingAssetActions = (asset, schema) => {
	const actionsContainer = document.getElementById('quick-scan-result-actions');
	const trackCheckInOut = schema.track_check_in_out_status || false;

	let html = '';

	if (trackCheckInOut) {
		// Calculate available quantity
		let availableQuantity = asset.quantity || 1;
		if (asset.groups) {
			asset.groups.forEach((group) => {
				if (group.quantity) availableQuantity -= group.quantity;
			});
		}

		// Show Check Out button if items available
		if (availableQuantity > 0) {
			html += `<button class="action-primary" id="quick-scan-action-checkout">
				<i class="fa fa-sign-out" style="margin-right:6px;"></i>Check Out
			</button>`;
		}

		// Show Check In button if items checked out
		if (asset.groups && asset.groups.length > 0) {
			html += `<button class="action-secondary" id="quick-scan-action-checkin">
				<i class="fa fa-sign-in" style="margin-right:6px;"></i>Check In
			</button>`;
		}
	}

	// Edit button
	html += `<button class="action-secondary" id="quick-scan-action-edit">
		<i class="fa fa-pencil" style="margin-right:6px;"></i>Edit
	</button>`;

	// View History button
	html += `<button class="action-tertiary" id="quick-scan-action-history">
		<i class="fa fa-history" style="margin-right:6px;"></i>History
	</button>`;

	actionsContainer.innerHTML = html;
	actionsContainer.className = 'quick-scan-result-actions';
};

const buildNewAssetActions = () => {
	const actionsContainer = document.getElementById('quick-scan-result-actions');

	actionsContainer.innerHTML = `
		<button class="action-primary" id="quick-scan-action-add" style="flex: 1 1 100%;">
			<i class="fa fa-plus" style="margin-right:6px;"></i>Add to Inventory
		</button>
	`;
	actionsContainer.className = 'quick-scan-result-actions single-action';
};

const fetchQuickScanPersonnel = () => {
	Parse.Cloud.run('fetchPersonnelForAssignment', { searchText: null, page: 0 })
		.then((personnel) => {
			quickScanAvailablePersonnel = personnel;
		})
		.catch((error) => {
			console.error('Error fetching personnel:', error);
		});
};

const hideQuickScanResult = () => {
	document.getElementById('quick-scan-result').style.display = 'none';
	document.getElementById('quick-scan-scan-again').style.display = 'none';
	quickScanCurrentAsset = null;
	quickScanCurrentScannedCode = null;
};

const handleQuickScanScanAgain = () => {
	hideQuickScanResult();
	lastScannedCode = null; // Reset to allow rescanning same code
	resetQuickScanState();
	startQuickScan();
};


/**
 * Quick Scan Action Handlers
 */

const handleQuickScanCheckout = () => {
	if (!quickScanCurrentAsset) return;

	const asset = quickScanCurrentAsset;
	const type = asset.type;
	const schema = assetsGlobalConfiguration.scan.type_schemas[type];
	const typeString = schema.display || type;

	// Calculate available quantity
	let availableQuantity = asset.quantity || 1;
	if (asset.groups) {
		asset.groups.forEach((group) => {
			if (group.quantity) availableQuantity -= group.quantity;
		});
	}

	// Close quick scan modal
	inputModule.hideModalWithId('quick-scan-modal');

	// Setup and show checkout modal (reuse existing modal)
	document.getElementById('asset-check-out-action-modal-title').innerHTML = `Check Out ${typeString}`;
	document.getElementById('asset-check-out-action-modal-description').innerHTML = `Check out this ${typeString} to a person.`;
	document.getElementById('asset-check-out-action-modal-remaining-label').innerHTML = availableQuantity === 1
		? '1 available to check out'
		: `${availableQuantity} available to check out`;

	// Reset inputs
	document.getElementById('asset-check-out-action-modal-quantity').value = '';
	document.getElementById('asset-check-out-action-modal-personnel').value = '';

	// Set personnel options
	let html = '<option selected value="">Select a Person</option>';
	quickScanAvailablePersonnel.forEach((personnel) => {
		html += `<option value="${personnel.id}">${personnel.name}</option>`;
	});
	document.getElementById('asset-check-out-action-modal-personnel').innerHTML = html;

	// Show modal
	inputModule.showModalWithId('asset-check-out-action-modal');
};

const handleQuickScanCheckin = () => {
	if (!quickScanCurrentAsset || !quickScanCurrentAsset.groups || quickScanCurrentAsset.groups.length === 0) return;

	const asset = quickScanCurrentAsset;
	const type = asset.type;
	const schema = assetsGlobalConfiguration.scan.type_schemas[type];
	const typeString = schema.display || type;

	// If only one group, check in directly
	if (asset.groups.length === 1) {
		const group = asset.groups[0];

		// Close quick scan modal
		inputModule.hideModalWithId('quick-scan-modal');

		// Setup and show checkin modal
		document.getElementById('asset-check-in-action-modal-title').innerHTML = `Check In ${typeString}`;
		document.getElementById('asset-check-in-action-modal-description').innerHTML =
			`Check in ${group.quantity} from ${group.personnel?.name || 'Unknown'}?`;

		// Store group ID for confirmation
		window.quickScanCheckInGroupId = group.id;

		inputModule.showModalWithId('asset-check-in-action-modal');
	} else {
		// Multiple groups - navigate to asset page for selection
		inputModule.hideModalWithId('quick-scan-modal');
		resetQuickScanState();
		editAssetWithId(asset.id);
	}
};

const handleQuickScanEdit = () => {
	if (!quickScanCurrentAsset) return;

	// Save the asset ID before resetting state
	const assetId = quickScanCurrentAsset.id;

	inputModule.hideModalWithId('quick-scan-modal');
	resetQuickScanState();
	editAssetWithId(assetId);
};

const handleQuickScanHistory = () => {
	if (!quickScanCurrentAsset) return;

	// Save the asset ID before resetting state
	const assetId = quickScanCurrentAsset.id;

	inputModule.hideModalWithId('quick-scan-modal');
	resetQuickScanState();
	appModule.handleRouting(`/dashboard/asset/activity/${assetId}`);
};

const handleQuickScanAddToInventory = () => {
	if (!quickScanCurrentScannedCode) return;

	// Save the scanned code before resetting state
	const scannedCode = quickScanCurrentScannedCode;

	inputModule.hideModalWithId('quick-scan-modal');
	resetQuickScanState();

	// Navigate to create new asset with pre-filled identifier
	appModule.handleRouting(`/dashboard/asset/new?identifier=${encodeURIComponent(scannedCode)}`);
};

const performQuickScanCheckout = async () => {
	const asset = quickScanCurrentAsset;
	if (!asset) return;

	const quantity = document.getElementById('asset-check-out-action-modal-quantity').value;
	const personnelId = document.getElementById('asset-check-out-action-modal-personnel').value;
	const quantityNumber = quantity ? parseInt(quantity, 10) : -1;

	const type = asset.type;
	const schema = assetsGlobalConfiguration.scan.type_schemas[type];
	const typeString = schema.display || type;

	// Validate
	if (!quantity || quantityNumber <= 0) {
		document.getElementById('asset-check-in-out-error-modal-title').innerHTML = `Check Out ${typeString}`;
		document.getElementById('asset-check-in-out-error-modal-description').innerHTML = 'Please enter the quantity to check out.';
		inputModule.showModalWithId('asset-check-in-out-error-modal');
		return;
	}
	if (!personnelId) {
		document.getElementById('asset-check-in-out-error-modal-title').innerHTML = `Check Out ${typeString}`;
		document.getElementById('asset-check-in-out-error-modal-description').innerHTML = 'Please select a person.';
		inputModule.showModalWithId('asset-check-in-out-error-modal');
		return;
	}

	// Disable buttons
	document.getElementById('asset-check-out-action-modal-confirm').disabled = true;
	document.getElementById('asset-check-out-action-modal-cancel').disabled = true;

	try {
		const group = await Parse.Cloud.run('updateCheckInOutStatusForAsset', {
			assetId: asset.id,
			action: 'check_out',
			quantity: quantityNumber,
			personnelId,
			platform: 'web'
		});

		if (group) {
			// Success - hide modal and reload assets
			inputModule.hideModalWithId('asset-check-out-action-modal');
			window.assetsShouldReloadAssets = true;
			resetQuickScanState();

			// Reload assets
			const typeArray = assetsGlobalAvailableTypes.map((t) => t.value);
			updateAssetsForUser(true, typeArray, 0);
		} else {
			throw new Error('Checkout failed');
		}
	} catch (error) {
		document.getElementById('asset-check-in-out-error-modal-title').innerHTML = `Check Out ${typeString}`;
		document.getElementById('asset-check-in-out-error-modal-description').innerHTML = 'An error occurred. Please try again.';
		inputModule.showModalWithId('asset-check-in-out-error-modal');
	} finally {
		document.getElementById('asset-check-out-action-modal-confirm').disabled = false;
		document.getElementById('asset-check-out-action-modal-cancel').disabled = false;
	}
};

const performQuickScanCheckin = async () => {
	const asset = quickScanCurrentAsset;
	const groupId = window.quickScanCheckInGroupId;

	if (!asset || !groupId) return;

	const type = asset.type;
	const schema = assetsGlobalConfiguration.scan.type_schemas[type];
	const typeString = schema.display || type;

	inputModule.hideModalWithId('asset-check-in-action-modal');

	try {
		const result = await Parse.Cloud.run('updateCheckInOutStatusForAsset', {
			assetId: asset.id,
			groupId,
			action: 'check_in',
			platform: 'web'
		});

		if (result && result.id) {
			// Success - reload assets
			window.assetsShouldReloadAssets = true;
			resetQuickScanState();

			// Reload assets
			const typeArray = assetsGlobalAvailableTypes.map((t) => t.value);
			updateAssetsForUser(true, typeArray, 0);
		} else {
			throw new Error('Checkin failed');
		}
	} catch (error) {
		document.getElementById('asset-check-in-out-error-modal-title').innerHTML = `Check In ${typeString}`;
		document.getElementById('asset-check-in-out-error-modal-description').innerHTML = 'An error occurred. Please try again.';
		inputModule.showModalWithId('asset-check-in-out-error-modal');
	}

	window.quickScanCheckInGroupId = null;
};


/**
 * Create Asset Modal Handlers
 */

const fetchBookDataFromGoogleBooks = async (isbn) => {
	try {
		const response = await fetch(`https://www.googleapis.com/books/v1/volumes?q=isbn:${isbn}`);
		if (!response.ok) return null;
		const data = await response.json();
		if (data.totalItems > 0 && data.items && data.items[0]) {
			const info = data.items[0].volumeInfo || {};
			return {
				source: 'Google Books',
				title: info.title || '',
				authors: info.authors || [],
				thumbnail: info.imageLinks ? info.imageLinks.thumbnail : null,
				isbn,
			};
		}
		return null;
	} catch (error) {
		console.error('Google Books fetch error:', error);
		return null;
	}
};

const fetchBookDataFromOpenLibrary = async (isbn) => {
	try {
		const response = await fetch(`https://openlibrary.org/isbn/${isbn}.json`);
		if (!response.ok) return null;
		const data = await response.json();
		return {
			source: 'Open Library',
			title: data.title || '',
			authors: (data.authors || []).map((a) => (a && a.name ? a.name : '')).filter(Boolean),
			thumbnail: data.covers && data.covers[0] ? `https://covers.openlibrary.org/b/id/${data.covers[0]}-L.jpg` : null,
			isbn,
		};
	} catch (error) {
		console.error('Open Library fetch error:', error);
		return null;
	}
};

const fetchBookData = async (isbn) => {
	const cleanIsbn = isbn.replace(/[-\s]/g, '');
	let bookData = await fetchBookDataFromGoogleBooks(cleanIsbn);
	if (!bookData) bookData = await fetchBookDataFromOpenLibrary(cleanIsbn);
	return bookData;
};

const resetCreateAssetForm = () => {
	createAssetScannedBookData = null;
	createAssetLastScannedCode = null;
	document.getElementById('create-asset-name').value = '';
	document.getElementById('create-asset-identifier').value = '';
	document.getElementById('create-asset-type').value = '';
	document.getElementById('create-asset-book-preview').style.display = 'none';
	document.getElementById('create-asset-loading').style.display = 'none';
	document.getElementById('create-asset-error').style.display = 'none';
};

const populateCreateAssetTypes = () => {
	const select = document.getElementById('create-asset-type');
	select.innerHTML = '<option selected value="">Select Asset Type</option>';

	if (assetsGlobalConfiguration && assetsGlobalConfiguration.scan && assetsGlobalConfiguration.scan.type_schemas) {
		const schemas = assetsGlobalConfiguration.scan.type_schemas;
		createAssetAvailableTypes = Object.keys(schemas).map((key) => ({
			value: key,
			display: schemas[key].display || key,
		}));

		createAssetAvailableTypes.forEach((type) => {
			const option = document.createElement('option');
			option.value = type.value;
			option.textContent = type.display;
			select.appendChild(option);
		});
	}
};

const displayBookPreview = (bookData) => {
	if (!bookData) return;

	createAssetScannedBookData = bookData;
	const previewEl = document.getElementById('create-asset-book-preview');

	// Update preview card
	document.getElementById('create-asset-book-source').textContent = bookData.source;
	document.getElementById('create-asset-book-title').textContent = bookData.title;
	document.getElementById('create-asset-book-authors').textContent = bookData.authors.join(', ');

	const thumbnail = document.getElementById('create-asset-book-thumbnail');
	if (bookData.thumbnail) {
		thumbnail.src = bookData.thumbnail;
		thumbnail.style.display = 'block';
	} else {
		thumbnail.style.display = 'none';
	}

	previewEl.style.display = 'block';

	// Auto-fill form fields
	if (bookData.title) {
		document.getElementById('create-asset-name').value = bookData.title;
	}
	if (bookData.isbn) {
		document.getElementById('create-asset-identifier').value = bookData.isbn;
	}
};

const showCreateAssetLoading = (show, message = 'Creating asset...') => {
	const loadingEl = document.getElementById('create-asset-loading');
	const loadingText = document.getElementById('create-asset-loading-text');
	loadingText.textContent = message;
	loadingEl.style.display = show ? 'block' : 'none';
};

const showCreateAssetError = (message) => {
	const errorEl = document.getElementById('create-asset-error');
	errorEl.textContent = message;
	errorEl.style.display = message ? 'block' : 'none';
};

const openCreateAssetModal = async () => {
	resetCreateAssetForm();
	populateCreateAssetTypes();
	inputModule.showModalWithId('create-asset-modal');
};

const closeCreateAssetModal = () => {
	inputModule.hideModalWithId('create-asset-modal');
	resetCreateAssetForm();
};

const handleCreateAssetSubmit = async () => {
	const assetName = document.getElementById('create-asset-name').value.trim();
	const assetIdentifier = document.getElementById('create-asset-identifier').value.trim();
	const assetType = document.getElementById('create-asset-type').value;

	// Validate
	if (!assetType) {
		showCreateAssetError('Please select an asset type.');
		return;
	}
	if (!assetName) {
		showCreateAssetError('Please enter an asset name.');
		return;
	}
	if (!assetIdentifier) {
		showCreateAssetError('Please enter an asset ID or ISBN.');
		return;
	}

	showCreateAssetError('');
	showCreateAssetLoading(true);

	try {

		// Get organization
		const organization = await userModule.handleConfigurationForUser();

		// Build asset data
		const assetData = {
			identifier: assetIdentifier,
			type: assetType,
			organizationId: organization.organization.id,
			assetData: {
				name: assetName,
			},
		};

		// Add book data if available
		if (createAssetScannedBookData) {
			assetData.assetData.bookTitle = createAssetScannedBookData.title;
			assetData.assetData.bookAuthors = createAssetScannedBookData.authors;
			assetData.assetData.bookThumbnail = createAssetScannedBookData.thumbnail;
			assetData.assetData.bookSource = createAssetScannedBookData.source;
		}

		// Save asset
		await Parse.Cloud.run('saveAssetWithParameters', {
			assetData,
			platform: 'web',
		});

		showCreateAssetLoading(false);
		closeCreateAssetModal();

		// Reload assets
		window.assetsShouldReloadAssets = true;
		loadAssets();
	} catch (error) {
		console.error('Create asset error:', error);
		showCreateAssetLoading(false);
		showCreateAssetError(error.message || 'An error occurred while creating the asset. Please try again.');
	}
};

const handleCreateAssetScanResult = async (scannedCode) => {

	// Prevent duplicate scans
	if (scannedCode === createAssetLastScannedCode) return;
	createAssetLastScannedCode = scannedCode;

	// Stop scanning
	scannerModule.stopScanning();

	// Show loading state
	document.getElementById('create-asset-scanner-video-container').style.display = 'none';
	document.getElementById('create-asset-scanner-status').style.display = 'block';
	document.getElementById('create-asset-scanner-status-text').textContent = 'Fetching book data...';

	// Clear any previous timeout
	if (createAssetScannerTimeout) clearTimeout(createAssetScannerTimeout);

	// Fetch book data
	try {
		const bookData = await fetchBookData(scannedCode);

		inputModule.hideModalWithId('create-asset-scanner-modal');
		resetCreateAssetScannerState();

		if (bookData) {
			displayBookPreview(bookData);
		} else {

			// No book found, just fill in the identifier
			document.getElementById('create-asset-identifier').value = scannedCode;
		}
	} catch (error) {
		console.error('Book data fetch error:', error);
		inputModule.hideModalWithId('create-asset-scanner-modal');
		resetCreateAssetScannerState();
		document.getElementById('create-asset-identifier').value = scannedCode;
	}
};

const resetCreateAssetScannerState = () => {
	createAssetLastScannedCode = null;
	const videoContainer = document.getElementById('create-asset-scanner-video-container');
	const statusEl = document.getElementById('create-asset-scanner-status');
	if (videoContainer) videoContainer.style.display = 'block';
	if (statusEl) statusEl.style.display = 'none';
	if (createAssetScannerTimeout) clearTimeout(createAssetScannerTimeout);
};

const startCreateAssetScanner = async () => {
	resetCreateAssetScannerState();

	// Check camera permission
	const permission = await scannerModule.checkCameraPermission();

	if (permission === 'denied') {
		inputModule.showModalWithId('quick-scan-camera-error-modal');
		return;
	}

	// Show scanner modal
	inputModule.showModalWithId('create-asset-scanner-modal');

	// Start scanning
	const videoElement = document.getElementById('create-asset-scanner-video');
	scannerModule.startScanning(videoElement, handleCreateAssetScanResult, (err) => {
		console.error('Scanner error:', err);
	});
};

const stopCreateAssetScanner = () => {
	scannerModule.stopScanning();
	inputModule.hideModalWithId('create-asset-scanner-modal');
	resetCreateAssetScannerState();
};


/**
 * Action Handlers
 */

const createActionHandlers = () => {

	// Handle click on add button
	$('#assets-add-asset').click(() => {
		addAsset();
	});

	// Handle click on load all button
	$('#assets-load-all').click(() => {
		helperModule.displayActionSheet('assets-action-dropdown');
		loadAllAssets();
	});

	// Handle click on export button
	$('#assets-export-assets').click(() => {
		helperModule.displayActionSheet('assets-action-dropdown');
		exportAssets();
	});

	// Handle click on check out export button
	$('#assets-export-checkout-report').click(() => {
		helperModule.displayActionSheet('assets-action-dropdown');
		exportCheckoutReport();
	});

	// Handle click on report button
	$('#assets-report-touchpoints').click(() => {
		helperModule.displayActionSheet('assets-action-dropdown');
		promptRunTouchpointReport();
	});

	// Handle click on run report button
	$('#assets-run-report').click(() => {
		helperModule.displayActionSheet('assets-action-dropdown');
		promptRunReport();
	});

	// Handle click on import button
	$('#assets-import-assets').click(() => {
		helperModule.displayActionSheet('assets-action-dropdown');
		importAssets();
	});

	// Handle click on remove button
	$('#assets-remove-button').click(() => {
		removeAssets();
	});

	// Handle click on quick action item
	$(document).off('click', '.quick-action-item');
	$(document).on('click', '.quick-action-item', function () {
		const actionId = $(this).data('action-id');
		selectQuickAction(actionId);
	});

	// Handle click on quick actions left navigation
	$('#assets-quick-actions-left').click(() => {
		const wrapper = document.getElementById('assets-quick-actions-wrapper');
		if (wrapper) {
			wrapper.scrollBy({ left: -110, behavior: 'smooth' });
		}
	});

	// Handle click on quick actions right navigation
	$('#assets-quick-actions-right').click(() => {
		const wrapper = document.getElementById('assets-quick-actions-wrapper');
		if (wrapper) {
			wrapper.scrollBy({ left: 110, behavior: 'smooth' });
		}
	});

	// Handle click on touchpoint modal run report button
	$('#asset-touchpoint-report-options-run').click(() => {
		exportAssetTouchpoints();
	});

	// Handle click on run report modal run button
	$('#asset-run-report-options-run').click(() => {
		exportRunReport();
	});

	// Handle click on modal cancel button
	$('#asset-archive-confirmation-cancel').click(() => {
		handleRemoveAssets(false);
	});

	// Handle click on modal cancel button
	$('#asset-touchpoint-report-options-cancel').click(() => {
		inputModule.hideModalWithId('asset-touchpoint-report-options-modal');
	});

	// Handle click on run report modal cancel button
	$('#asset-run-report-options-cancel').click(() => {
		inputModule.hideModalWithId('asset-run-report-options-modal');
	});

	// Handle click on modal remove button
	$('#asset-archive-confirmation-remove').click(() => {
		handleRemoveAssets(true);
	});

	// Handle click on modal confirm button
	$('#asset-archive-error-modal-confirm').click(() => {
		inputModule.hideModalWithId('asset-archive-error-modal');
	});

	// Handle click on modal done button
	$('#asset-qr-code-done').click(() => {
		inputModule.hideModalWithId('asset-qr-code-modal');
	});

	// Handle click on modal print button
	$('#asset-qr-code-print').click(() => {
		const printWindow = window.open('', 'PRINT', 'height=400,width=600');
		printWindow.document.write(`<html><head><title>${document.title}</title>`);
		printWindow.document.write('<link href="https://fonts.googleapis.com"><link href="https://fonts.gstatic.com" crossorigin><link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800;900&display=swap" rel="stylesheet"></link>');
		printWindow.document.write(`
			<style type="text/css">
				.qr-print-area {
					display: flex;
					flex-direction: column;
					align-items: center;
					gap: 10px;
					padding: 30px 0;
				}
				.qr-print-area h3 {
					text-align: center;
					color: black;
					margin-top: -8px;
					font-size: 1.4rem;
					font-family: 'Poppins', Verdana, Arial, Helvetica, sans-serif !important;
				}
				.qr-scan-me-badge {
					display: inline-block;
					background-color: #333;
					color: white;
					padding: 6px 16px;
					border-radius: 20px;
					font-size: 0.85rem;
					font-weight: 600;
					letter-spacing: 0.5px;
					font-family: 'Poppins', Verdana, Arial, Helvetica, sans-serif !important;
				}
				.qr-code-container {
					text-align: center;
					border: 5px solid black;
					display: inline-flex;
					border-radius: 20px;
				}
				.qr-code-container img {
					background-color: white;
					border-radius: 15px;
					width: 200px;
					height: 200px;
					position: relative;
					z-index: 2;
				}
			</style>
		`);
		printWindow.document.write('</head><body >');
		printWindow.document.write(document.getElementById('qr-print-area').innerHTML);
		printWindow.document.write('</body></html>');
		printWindow.document.close(); // necessary for IE >= 10
		printWindow.focus(); // necessary for IE >= 10
		setTimeout(() => {
			printWindow.print();
			printWindow.close();
		}, 1000);
	});

	// Handle click on quick scan button
	$('#assets-quick-scan').click(() => {
		startQuickScan();
	});

	// Handle click on quick scan cancel button
	$('#quick-scan-cancel').click(() => {
		stopQuickScan();
	});

	// Handle click on camera error confirm button
	$('#quick-scan-camera-error-confirm').click(() => {
		inputModule.hideModalWithId('quick-scan-camera-error-modal');
	});

	// Handle click on not found cancel button
	$('#quick-scan-not-found-cancel').click(() => {
		inputModule.hideModalWithId('quick-scan-not-found-modal');
	});

	// Handle click on not found retry button
	$('#quick-scan-not-found-retry').click(() => {
		inputModule.hideModalWithId('quick-scan-not-found-modal');
		startQuickScan();
	});

	// Handle click on scan again button
	$('#quick-scan-scan-again').click(() => {
		handleQuickScanScanAgain();
	});

	// Handle quick scan action button clicks (delegated)
	$(document).on('click', '#quick-scan-action-checkout', () => {
		handleQuickScanCheckout();
	});

	$(document).on('click', '#quick-scan-action-checkin', () => {
		handleQuickScanCheckin();
	});

	$(document).on('click', '#quick-scan-action-edit', () => {
		handleQuickScanEdit();
	});

	$(document).on('click', '#quick-scan-action-history', () => {
		handleQuickScanHistory();
	});

	$(document).on('click', '#quick-scan-action-add', () => {
		handleQuickScanAddToInventory();
	});

	// Handle check-in modal buttons (for quick scan)
	$('#asset-check-in-action-modal-cancel').click(() => {
		inputModule.hideModalWithId('asset-check-in-action-modal');
		window.quickScanCheckInGroupId = null;
	});

	$('#asset-check-in-action-modal-confirm').click(() => {
		if (window.quickScanCheckInGroupId) {
			performQuickScanCheckin();
		}
	});

	// Handle check-out modal buttons (for quick scan)
	$('#asset-check-out-action-modal-cancel').click(() => {
		inputModule.hideModalWithId('asset-check-out-action-modal');
	});

	$('#asset-check-out-action-modal-confirm').click(() => {
		if (quickScanCurrentAsset) {
			performQuickScanCheckout();
		}
	});

	// Handle check-in/out error modal
	$('#asset-check-in-out-error-modal-confirm').click(() => {
		inputModule.hideModalWithId('asset-check-in-out-error-modal');
	});

	// Handle click on quick add button
	$('#assets-quick-add').click(() => {
		openCreateAssetModal();
	});

	// Handle click on create asset cancel button
	$('#create-asset-cancel').click(() => {
		closeCreateAssetModal();
	});

	// Handle click on create asset submit button
	$('#create-asset-submit').click(() => {
		handleCreateAssetSubmit();
	});

	// Handle click on scan ISBN button
	$('#create-asset-scan-isbn').click(() => {
		startCreateAssetScanner();
	});

	// Handle click on create asset scanner cancel button
	$('#create-asset-scanner-cancel').click(() => {
		stopCreateAssetScanner();
	});

	// Handle click on asset selector
	$(document).off('click', "[id^='assets-table-load-more-']");
	$(document).on('click', "[id^='assets-table-load-more-']", function () {
		loadMoreAssets($(this).attr('id').replace('assets-table-load-more-', ''));
	});

	// Handle click on asset selector
	$(document).off('click', "[id^='asset-selector-']");
	$(document).on('click', "[id^='asset-selector-']", function (event) {
		if (!isDragging) {
			selectAssetRow(event, $(this).data('asset-id'));
			isDragging = false;
		}
	});

	// Handle click on QR Code
	$(document).off('click', "[id^='asset-qr-icon-']");
	$(document).on('click', "[id^='asset-qr-icon-']", async function (event) {
		event.stopPropagation();

		// Get asset ID
		const assetId = $(this).data('asset-id');

		// Get asset data
		let assetData = null;
		const assets = await userModule.getAssetsForUser(false);
		for (let i = 0; i < assets.length; i += 1) {
			if (assets[i].id === assetId) {
				assetData = assets[i];
				break;
			}
		}

		// Fetch configuration for user
		const config = await userModule.handleConfigurationForUser(false);
		assetsGlobalConfiguration = JSON.parse(config);

		// Get schema types
		const typeSchemas = assetsGlobalConfiguration.display.type_schemas;
		const schema = typeSchemas[assetData.type];
		const displayType = schema.display;

		// Generate QR code
		const code = await QRCode.toDataURL(`https://${process.env.ENTITY_OBJECT.DOMAIN}/_asset/${assetId}/19657af9-ba53-4eb7-933b-81c2658c26a3`, {
			scale: 10
		});

		// Set parameters
		$('#asset-qr-code-img').attr('src', code);
		$('#asset-qr-code-description').html(`${displayType} ${assetData.identifier}`);

		// Show status modal
		inputModule.showModalWithId('asset-qr-code-modal');
	});

	// Handle click on asset row
	$(document).off('click', "[name='asset-row-element']");
	$(document).on('click', "[name='asset-row-element']", function () {
		if (!isDragging) {
			editAssetWithId($(this).data('asset-id'));
			isDragging = false;
		}
	});

	// Handle mouse enter on asset row (for tooltip)
	$(document).off('mouseenter', "[name='asset-row-element']");
	$(document).on('mouseenter', "[name='asset-row-element']", function (event) {
		const assetId = $(this).data('asset-id');
		if (assetId) {
			showTooltip(assetId, event);
		}
	});

	// Handle mouse move on asset row (to track cursor)
	$(document).off('mousemove', "[name='asset-row-element']");
	$(document).on('mousemove', "[name='asset-row-element']", function (event) {
		const assetId = $(this).data('asset-id');
		if (assetId && tooltipCurrentAssetId === assetId) {
			positionTooltip(event);
		}
	});

	// Handle mouse leave on asset row (hide tooltip)
	$(document).off('mouseleave', "[name='asset-row-element']");
	$(document).on('mouseleave', "[name='asset-row-element']", function () {
		hideTooltip();
	});

	// Handle click on asset row sort element
	$(document).off('click', "[name='asset-row-sort-element']");
	$(document).on('click', "[name='asset-row-sort-element']", function () {
		if (!isDragging) {
			performAssetSort($(this).data('type-value'), $(this).data('offset'));
			isDragging = false;
		}
	});

	// Handle click on action menu
	$('#assets-action-menu').click(() => {
		helperModule.displayActionSheet('assets-action-dropdown');
	});

	// Handle scroll asset table
	document.addEventListener('scroll', (event) => {
		if (event.target != null && event.target.id != null) {
			if (event.target.id.indexOf('assets-table-view-') > -1) {
				const value = event.target.id.replace('assets-table-view-', '');
				assetsGlobalTableState[value].scroll = $(`#${event.target.id}`).scrollLeft();
			}
		}
	}, true);
};


/**
 * Input Handlers
 */

const createInputHandlers = () => {

	// Handle key up on search
	$('#asset-search-input-large').keyup(function () {
		performAssetSearch(this);
	});

	// Handle change filter
	$('#assets-type-filter').change(function () {
		filterAssetType(this);
	});

	// Handle quick actions wrapper scroll
	$('#assets-quick-actions-wrapper').scroll(() => {
		updateQuickActionScrollState();
	});

	// Handle window resize for quick actions
	$(window).resize(() => {
		updateQuickActionScrollState();
	});

	// Handle drag start for table
	$(document).off('mousedown', "[id^='assets-table-view-']");
	$(document).on('mousedown', "[id^='assets-table-view-']", function (e) {

		// Stop propagation
		e.stopPropagation();
		e.preventDefault();

		// Set state
		dragDown = true;
		isDragging = false;
		dragX = e.pageX;
		dragY = e.pageY;
		dragTop = $(this).scrollTop();
		dragLeft = $(this).scrollLeft();
		dragElement = this;

		// Update cursor
		$(this).addClass('grab');
	});

	// Handle drag for table
	$(document).off('mousemove', dragElement);
	$(document).on('mousemove', dragElement, (e) => {
		if (dragDown) {

			// Update state
			const newX = e.pageX;
			const newY = e.pageY;
			isDragging = true;

			// Scroll element
			$(dragElement).scrollTop(dragTop - newY + dragY);
			$(dragElement).scrollLeft(dragLeft - newX + dragX);
		}
	});

	// Handle drag stop for table
	$(document).off('mouseup', dragElement);
	$(document).on('mouseup', dragElement, (e) => {
		if (dragDown) {

			// Stop propagation
			e.stopPropagation();
			e.preventDefault();

			// Update cursor
			$(dragElement).removeClass('grab');

			// Reset state
			dragDown = false;
			dragElement = null;
		}
	});
};


/**
 * State Handlers
 */

exports.handlerDidLoad = async () => {

	// Create action handlers
	createActionHandlers();

	// Create input handlers
	createInputHandlers();

	// Initialize tooltip elements
	initializeTooltip();

	// Initialize quick actions scroll state
	updateQuickActionScrollState();

	// Check for visible activity indicator
	inputModule.checkElementVisible('assets-activity-indicator', async () => {

		// Initialize global asset array
		window.assetsGlobalAssetArray = [];

		// Reset variables
		assetsGlobalSelectedFilter = null;
		assetsGlobalSelectedSortDict = null;
		assetsGlobalTableState = null;

		// Fetch configuration for user
		const config = await userModule.handleConfigurationForUser(false);
		assetsGlobalConfiguration = JSON.parse(config);

		// Get available types
		const typeArray = [];
		assetsGlobalAvailableTypes = [];
		assetsGlobalSelectedSortDict = {};
		assetsGlobalTableState = {};
		Object.keys(assetsGlobalConfiguration.display.type_schemas).forEach((property) => {
			if (Object.prototype.hasOwnProperty.call(assetsGlobalConfiguration.display.type_schemas, property)) {
				typeArray.push(property);
				assetsGlobalAvailableTypes.push({
					value: property,
					display: assetsGlobalConfiguration.display.type_schemas[property].display
				});
				assetsGlobalSelectedSortDict[property] = {
					column: 0,
					reverse: false
				};
				assetsGlobalTableState[property] = {
					scroll: 0
				};
			}
		});

		// Set up filter
		setupAssetFilter();

		// Get assets for user
		updateAssetsForUser(true, typeArray, 0);
	});
};

exports.handlerDidAppear = async () => {

	// Update asset display
	if (window.assetsShouldReloadAssets === true) {

		// Initialize dictionaries
		window.assetsGlobalAssetDictionary = {};
		window.assetsGlobalDisplayDictionary = {};

		// Set assets to dictionaries
		for (let i = 0; i < window.assetsGlobalAssetArray.length; i += 1) {
			const asset = window.assetsGlobalAssetArray[i];

			// Set assets
			let typeArray = window.assetsGlobalAssetDictionary[asset.type];
			if (typeArray == null) typeArray = [];
			typeArray.push(asset);
			window.assetsGlobalAssetDictionary[asset.type] = typeArray;
		}

		// Sort type array by largest number of assets
		assetsGlobalAvailableTypes.sort((a, b) => {
			if (window.assetsGlobalAssetDictionary[a.value] == null) window.assetsGlobalAssetDictionary[a.value] = [];
			if (window.assetsGlobalAssetDictionary[b.value] == null) window.assetsGlobalAssetDictionary[b.value] = [];
			return window.assetsGlobalAssetDictionary[b.value].length - window.assetsGlobalAssetDictionary[a.value].length;
		});

		// Perform search and update
		performAssetSearch($('#asset-search-input-large')[0]);

		// Update state
		window.assetsShouldReloadAssets = false;

		// Fetch organizations for user
		const { organization } = await externalUserModule.fetchConfigurationForUser();

		// Update element display
		if (organization.id === 'LwPkCDay4Y') {
			$('#assets-export-checkout-report').show();
		} else {
			$('#assets-export-checkout-report').hide();
		}
	}
};
